import React, { useCallback, useState, useRef, useMemo } from 'react'
import { ConfirmationModal } from './ConfirmationModal'
import { TConfirmationData, IConfirmationOption } from './types'

function defaultMessageCreator(message: string | null) {
  return typeof message === 'string' ? message : 'Are you sure?'
}

type TPromise = {
  resolve(): void
  reject(): void
}

export const useConfirmation = <T,>(
  cb: (data: T) => Promise<void> | void,
  options: Partial<IConfirmationOption<T>>
): [(data: TConfirmationData<T>) => Promise<void>, JSX.Element] => {
  const opt = useMemo<IConfirmationOption<T>>(
    () => ({
      header: 'Are yoy sure?',
      cancelText: 'Cancel',
      actionText: 'Ok',
      createMessage: defaultMessageCreator,
      condition: () => true,
      ...options
    }),
    [options]
  )

  const args = useRef<T>()

  const promiseRef = useRef<TPromise>()

  const [content, setContent] = useState<string | null>(null)

  const handleClose = useCallback(() => {
    // document.body.style.height = 'unset'
    // document.querySelector('html')?.removeAttribute('style')
    setContent(null)
  }, [])

  const handleCancel = useCallback(() => {
    handleClose()
    if (promiseRef.current) {
      promiseRef.current.reject()
    }
  }, [handleClose])

  const handleConfirm = useCallback<(data: TConfirmationData<T>) => Promise<void>>(({ data, message }) => {
    /** Only for this project */
    // document.body.style.height = 'auto'
    // document.querySelector('html')?.setAttribute('style', 'overflow: hidden')
    /** ---- end section ---- */
    args.current = data
    return new Promise((resolve, reject) => {
      if (typeof opt.condition === 'function' && opt.condition(data)) {
        promiseRef.current = { resolve, reject }
        setContent(message)
      } else {
        cb(data)
      }
    })
  }, [cb, opt])

  const handleAction = useCallback(async () => {
    if (typeof args.current !== 'undefined') {
      await cb(args.current)
    }
    handleClose()
    if (promiseRef.current) {
      promiseRef.current.resolve()
    }
  }, [cb, handleClose])

  return [
    handleConfirm,
    <ConfirmationModal
      key={opt.header}
      header={opt.header}
      isOpen={typeof content === 'string'}
      setClose={handleCancel}
      confirmationText={opt.createMessage(content, args.current)}
      onAction={handleAction}
      actionText={opt.actionText}
      cancelText={opt.cancelText}
    />
  ]
}
